import { Link, Page, Text, View } from '@react-pdf/renderer'
import { FfsReportSectionId } from 'pages/reporting/reports/ffs/ffsReportData'
import { useFfsReportOps } from 'pages/reporting/reports/ffs/FfsReportProvider'
import { ffsPageStyle } from 'pages/reporting/reports/ffs/styles/ffsPDFStyles'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'pages/reporting/shared/pdfGeneration/components/shared/PageHeader'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import { TOCSection, TOCSectionProps } from 'pages/reporting/shared/types'
import React, { useMemo } from 'react'

/**
 * TOC Entry component - matches rooftop report styling pattern
 */
const TOCEntry: React.FC<{
  title: string
  pageNumber?: number
  linkId?: string
  level?: 1 | 2 | 3
}> = ({ title, pageNumber, linkId, level = 1 }) => {
  const isSubEntry = level > 1
  const textColor = isSubEntry ? '#6b7280' : '#1f2937'
  const fontWeight = isSubEntry ? 'normal' : 'bold'

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-end',
        marginBottom: 8,
        marginLeft: (level - 1) * 12,
      }}>
      {linkId ? (
        <Link src={`#${linkId}`} style={{ textDecoration: 'none' }}>
          <Text
            style={{
              fontSize: 11,
              color: textColor,
              fontWeight: fontWeight,
            }}>
            {title}
          </Text>
        </Link>
      ) : (
        <Text
          style={{
            fontSize: 11,
            color: textColor,
            fontWeight: fontWeight,
          }}>
          {title}
        </Text>
      )}
      <Text
        style={{
          flex: 1,
          fontSize: 8,
          color: '#d1d5db',
          marginHorizontal: 4,
          overflow: 'hidden',
          lineHeight: 1,
        }}>
        {'.'.repeat(200)}
      </Text>
      {pageNumber !== undefined && (
        <Text
          style={{
            fontSize: 11,
            color: isSubEntry ? '#9ca3af' : '#4b5563',
            width: 25,
            textAlign: 'right',
          }}>
          {pageNumber}
        </Text>
      )}
    </View>
  )
}

const RenderPDF: React.FC<TOCSectionProps> = ({
  entries,
  sectionPages,
  onRender,
}) => {
  const { formData, indicationsWithReports } = useFfsReportOps()

  const mainInfo = useMemo(() => formData.mainInfo, [formData.mainInfo])

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  return (
    <Page size='LETTER' style={ffsPageStyle} id='tableOfContents' wrap={true}>
      {/* Page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: FfsReportSectionId.TABLE_OF_CONTENTS, pageNumber })
          return ''
        }}
      />

      <PageHeader title='Table of Contents' />

      {/* Content with proper bottom padding to prevent footer clipping */}
      <View style={[PDFStyles.content, { paddingBottom: 70 }]}>
        <Text
          style={{
            fontSize: 18,
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: 20,
            textAlign: 'center',
          }}>
          Table of Contents
        </Text>

        {entries.map((section) => {
          const basePageNumber = sectionPages[section.id]

          if (section.id === FfsReportSectionId.ASSESSMENT_RESULTS) {
            return (
              <React.Fragment key={section.id}>
                {/* Main Assessment Results entry */}
                <TOCEntry
                  title={section.title}
                  pageNumber={basePageNumber}
                  linkId={section.id}
                  level={1}
                />
                {/* Sub-entries: one per indication */}
                {indicationsWithReports.map((item, index) => {
                  const subId =
                    index === 0
                      ? FfsReportSectionId.ASSESSMENT_RESULTS
                      : `${FfsReportSectionId.ASSESSMENT_RESULTS}-${index}`
                  const subPageNumber = sectionPages[subId]
                  const linkId = `assessment-${item.indication.id}`

                  return (
                    <TOCEntry
                      key={linkId}
                      title={`Indication ${index + 1}`}
                      pageNumber={subPageNumber}
                      linkId={linkId}
                      level={2}
                    />
                  )
                })}
              </React.Fragment>
            )
          }

          return (
            <TOCEntry
              key={section.id}
              title={section.title}
              pageNumber={basePageNumber}
              linkId={section.id}
              level={1}
            />
          )
        })}
      </View>

      <PageFooter
        jobNumber={mainInfo.assetId}
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={isPrelim}
      />
    </Page>
  )
}

export const TableOfContents: TOCSection = {
  RenderPDF,
  id: FfsReportSectionId.TABLE_OF_CONTENTS,
  title: 'Table of Contents',
}
